import { useState, useEffect, useRef, useCallback } from "react";

export interface DialogueLine {
  speaker: string;
  text: string;
  showInput?: "text" | "password";
  inputPlaceholder?: string;
  choices?: DialogueChoice[];
}

export interface DialogueChoice {
  label: string;
  action: () => void;
}

export interface UseDialogueOptions {
  typingSpeed?: number; // ms per character
  onTypingComplete?: () => void;
}

export interface UseDialogueReturn {
  displayedText: string;
  isTyping: boolean;
  showChoices: boolean;
  showInput: boolean;
  skipTyping: () => void;
  resetDialogue: () => void;
}

/**
 * 비주얼 노벨 스타일 대화 타이핑 효과 훅
 */
export function useDialogue(
  dialogue: DialogueLine | null,
  options: UseDialogueOptions = {}
): UseDialogueReturn {
  const { typingSpeed = 30, onTypingComplete } = options;

  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const indexRef = useRef(0);
  // 초기값을 undefined로 설정하여 첫 렌더링 시 ""인 경우에도 리셋되도록 함
  const prevTextRef = useRef<string | undefined>(undefined);

  // 현재 대화 텍스트 (안정적인 참조용)
  const dialogueTextRef = useRef(dialogue?.text ?? "");
  dialogueTextRef.current = dialogue?.text ?? "";

  // 타이핑 완료 처리
  const completeTyping = useCallback(() => {
    const text = dialogueTextRef.current;
    if (!text) return;

    setDisplayedText(text);
    setIsTyping(false);

    // dialogue 객체를 직접 참조하지 않고 현재 상태로 확인
    if (dialogue?.choices) {
      setTimeout(() => setShowChoices(true), 150);
    }
    if (dialogue?.showInput) {
      setTimeout(() => setShowInput(true), 150);
    }

    onTypingComplete?.();
  }, [dialogue?.choices, dialogue?.showInput, onTypingComplete]);

  // 타이핑 스킵
  const skipTyping = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    completeTyping();
  }, [completeTyping]);

  // 리셋
  const resetDialogue = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    setDisplayedText("");
    setIsTyping(false);
    setShowChoices(false);
    setShowInput(false);
    indexRef.current = 0;
  }, []);

  // 대화 변경 시 타이핑 시작
  // ⚠️ dialogue.text를 기준으로 변경 감지 (객체 참조가 아닌 텍스트 내용으로)
  const currentText = dialogue?.text ?? null;

  useEffect(() => {
    // 텍스트가 없으면 리셋하고 종료
    if (!currentText) {
      resetDialogue();
      prevTextRef.current = currentText;
      return;
    }

    prevTextRef.current = currentText;

    const text = currentText;

    // 리셋 및 타이핑 시작
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    // 상태 초기화
    setDisplayedText("");
    setShowChoices(false);
    setShowInput(false);
    indexRef.current = 0;
    setIsTyping(true);

    const typeChar = () => {
      if (indexRef.current < text.length) {
        setDisplayedText(text.slice(0, indexRef.current + 1));
        indexRef.current++;
        typingTimeoutRef.current = setTimeout(typeChar, typingSpeed);
      } else {
        completeTyping();
      }
    };

    // 50ms 정도 짧은 지연 후 시작 (마운트와 타이핑 시작이 겹치지 않게)
    const startTimeout = setTimeout(typeChar, 50);

    return () => {
      clearTimeout(startTimeout);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentText, typingSpeed]); // completeTyping, resetDialogue 제거하여 애니메이션 중단 방지

  return {
    displayedText,
    isTyping,
    showChoices,
    showInput,
    skipTyping,
    resetDialogue,
  };
}
